/**
 * Copyright (c) 2018, salesforce.com, inc.
 * All rights reserved.
 * Licensed under the BSD 3-Clause license.
 * For full license text, see LICENSE.txt file in the repo root or
 * https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { withRouter, Link, matchPath } from 'react-router-dom';


const TabList = styled.ul`
  height: 100%;
  width: 100%;

  display: flex;
  justify-content: flex-start;
  align-items: center;

  border-bottom: 1px solid #d8dde6;


  @media (max-width: 767px) {
    padding-left: .25rem;
    width: 100vw;
    overflow-x: auto;
    overflow-style: auto;
  }

  &::-webkit-scrollbar {
    display: none;
  }

  &::scrollbar {
    display: none;
  }
`;

const TabItem = styled.li`
  height: 100%;

  margin: 0 .5rem;

  @media (min-width: 1024px) {
    margin-left: 1rem;
  }

  border-bottom: solid 2px ${(p) => p.isActive ? '#0070D2' : 'transparent'};

  display: flex;
  align-items: center;

  &:hover, &:focus {
    color: #0070D2;
    cursor: pointer;
    border-bottom: solid 2px #0070D2;
  };
`;

// Exclude isActive from props passed to Link
const TabLink = styled(({ isActive, ...rest }) => <Link {...rest} />)`
  height: 100%;
  display: flex;
  align-items: center;
  max-width: 100%;
  padding: 0 0.5rem;
  font-size: 12px;
  letter-spacing: 1px;
  text-transform: uppercase;

  color: ${(p) => p.isActive ? '#16325C' : '#54698D'};

  overflow: hidden;
  text-overflow: ellipsis;
  white-space: pre-line;
  cursor: pointer;
  text-decoration: none;
  text-align: center;
  &:focus, &:hover {
    text-decoration: none;
    outline: 0;
    color: #0070D2;
  }
`;


/**
 * Veritcal Navbar used for site navigation
 */
export const Tabs = ({ items, className, location, onClick }) => {
  const fullPath = location.search ? location.pathname + location.search : location.pathname;
  return (
    <TabList className={className} >
      {items.map((item, i) => {
        const isActive = matchPath(fullPath, { path: item.url, exact: true });
        return (
          <TabItem key={i} isActive={isActive}>
            <TabLink
              onClick={ onClick }
              to={item.to || item.url}
              isActive={isActive}
            >
              {item.name}
            </TabLink>
          </TabItem>
        );
      })}
    </TabList>
  );
};

Tabs.propTypes = {
  className: PropTypes.string,
  items: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.object,
    ]),
    url: PropTypes.string,
  })),
  onClick: PropTypes.func,
  location: PropTypes.shape({
    pathname: PropTypes.string
  })
};

export default withRouter(Tabs);
